import { useIssueNumber } from "@/hooks/useIssueNumber";
import { NOTIFY_ON_READY } from "@/lib/featureFlags";
import PendingStatusStrip from "./PendingStatusStrip";
import { ResultsTopNav } from "./ResultsTopNav";

interface Props {
  textDescription: string;
  occasion: string | null;
  season: string | null;
  outfitId: string | null;
  isGuest: boolean;
  onBack: () => void;
}

/**
 * Optimistic loading shell for the text-description flow. Mirrors
 * UrlPendingResults visually; transitions to full Results once the
 * watcher sees analysis_status='items_ready'.
 */
export default function TextPendingResults({
  textDescription,
  occasion,
  season,
  outfitId,
  isGuest,
  onBack,
}: Props) {
  const issueNumber = useIssueNumber(outfitId, isGuest);
  const preview =
    textDescription.length > 80
      ? textDescription.slice(0, 77) + "…"
      : textDescription;
  const chips = [occasion, season].filter((c): c is string => !!c);

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#FAF6EF",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <style>{`
        @keyframes l2f-text-pending-pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.55; }
        }
        @keyframes l2f-text-pending-spin { to { transform: rotate(360deg); } }
      `}</style>
      <ResultsTopNav
        issueNumber={issueNumber}
        isSaved={false}
        onBack={onBack}
        onBookmarkTap={onBack}
      />
      <PendingStatusStrip
        status="analysing"
        notifyEnabled={NOTIFY_ON_READY}
      />
      <div style={{ padding: "16px 16px 96px" }}>
        <div
          style={{
            width: "100%",
            aspectRatio: "3 / 4",
            background: "#FAECE7",
            border: "0.5px solid #E8E2D9",
            borderRadius: 20,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 16,
            padding: 28,
            textAlign: "center",
            animation: "l2f-text-pending-pulse 1.6s ease-in-out infinite",
          }}
        >
          <i
            className="ti ti-loader-2"
            style={{
              fontSize: 28,
              color: "#C84B2F",
              opacity: 0.7,
              animation: "l2f-text-pending-spin 1s linear infinite",
            }}
          />
          <div
            style={{
              fontSize: 22,
              fontStyle: "italic",
              fontFamily:
                "var(--font-serif), 'Instrument Serif', Georgia, serif",
              color: "#2C2820",
              lineHeight: 1.25,
              maxWidth: 280,
            }}
          >
            “{preview}”
          </div>
          <div
            className="l2f-type-caption"
            style={{
              color: "#6B6560",
              maxWidth: 240,
              lineHeight: 1.5,
            }}
          >
            Crafting your look…
          </div>
          {chips.length > 0 && (
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", justifyContent: "center" }}>
              {chips.map((c) => (
                <span
                  key={c}
                  style={{
                    padding: "4px 10px",
                    borderRadius: 999,
                    background: "#FFFCF8",
                    fontSize: 11,
                    color: "#4A4540",
                    textTransform: "capitalize",
                    border: "0.5px solid #E8E2D9",
                  }}
                >
                  {c}
                </span>
              ))}
            </div>
          )}
        </div>

        <div style={{ marginTop: 16, display: "flex", flexDirection: "column", gap: 10 }}>
          {[0, 1, 2, 3].map((i) => (
            <div
              key={i}
              style={{
                height: 64,
                borderRadius: 14,
                background: "#FFFCF8",
                border: "0.5px solid #E8E2D9",
                animation: "l2f-text-pending-pulse 1.6s ease-in-out infinite",
                animationDelay: `${i * 0.12}s`,
              }}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
